import { ListTable } from '../../../../../components/table/ListTable';
import { columnDescriptorsLists, EActionType } from './columnDescriptorsLists';
import { InterruptionVinUpload, VinBlacklistModel } from './InterruptionVinUpload';
import ConfirmationDialog from '@rio-cloud/rio-uikit/ConfirmationDialog';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import { ChangeEvent, useState } from 'react';
import {
    useChangeVinListStateMutation,
    useGetAllLogsQuery,
    useGetAllVinListsQuery,
} from '../../../../../services/connectivityInterruptionApi';
import { columnDescriptorsLogs } from './columnDescriptorsLogs';
import { FormattedMessage, useIntl } from 'react-intl';

export const InterruptionList = () => {
    const { formatMessage } = useIntl();
    const { data: vinListItems = [], isLoading: isLoadingVinLists, error } = useGetAllVinListsQuery();
    const { data: logItems = [], isLoading: isLoadingLogList } = useGetAllLogsQuery();
    const [changeVinListState, { isLoading: isChangingVinList }] = useChangeVinListStateMutation();
    const [actionType, setActionType] = useState<EActionType>();
    const [currentItem, setCurrentItem] = useState<VinBlacklistModel>();
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
    const [reason, setReason] = useState('');
    const [formData, setFormData] = useState(() => ({
        reason: '',
    }));
    const [errors, setErrors] = useState<{ reason?: string }>({});

    const onReasonChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setFormData((prev) => ({ ...prev, reason: value }));
        if (errors.reason) {
            setErrors((prev) => ({ ...prev, reason: undefined }));
        }
    };

    const validateForm = () => {
        const newErrors: { reason?: string } = {};
        if (!formData.reason) {
            newErrors.reason = formatMessage({
                id: 'intl-msg:coreconAdminWeb.connectivityInterruption.reason.validation.missing',
            });
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleConfirmation = (action: EActionType, item: VinBlacklistModel) => {
        setActionType(action);
        setCurrentItem(item);
        setReason('');
        setShowConfirmationDialog(true);
    };

    const handleActionConfirmed = async () => {
        if (!currentItem || !actionType) return;

        const isValid = validateForm();
        if (!isValid) return;

        const model: VinBlacklistModel = {
            reason: formData.reason,
            listName: currentItem.listName,
            action: actionType === EActionType.BLACKLIST ? 'BLACKLIST' : 'WHITELIST',
        };

        try {
            await changeVinListState(model).unwrap();
        } catch (err) {
            console.error('Failed to update vin list state', err);
        }
        setShowConfirmationDialog(false);
        setFormData({ reason: '' });
        setCurrentItem(undefined);
    };

    if (isLoadingVinLists || isLoadingLogList) return <div>Loading...</div>;
    if (error) return <div>Failed to load VIN lists.</div>;

    return (
        <>
            <div className="margin-top-20">
                <InterruptionVinUpload />
                <h3>
                    <FormattedMessage id="intl-msg:coreconAdminWeb.connectivityInterruption.uploaded.vin.list" />
                </h3>
                <ListTable<VinBlacklistModel>
                    items={vinListItems}
                    showHeader
                    itemKey="listName"
                    columnDescriptors={columnDescriptorsLists(handleConfirmation)}
                />
            </div>
            <ConfirmationDialog
                show={showConfirmationDialog}
                title={`Confirm ${actionType === EActionType.BLACKLIST ? 'Blacklist' : 'Whitelist'} Action`}
                content={
                    <>
                        <p>
                            You are about to {actionType === EActionType.BLACKLIST ? 'Blacklist' : 'Whitelist'}
                            all SIM cards associated with the VINs in <strong>{currentItem?.listName}</strong>. Are you
                            sure?
                        </p>
                        <div className="form-group margin-top-10">
                        <div className={'form-group' + (errors.reason ? ' has-feedback has-error' : '')}>
                            <label htmlFor="reason">
                                <span>
                                    <FormattedMessage
                                    id={'intl-msg:coreconAdminWeb.connectivityInterruption.reason'} /> *
                                </span>
                            </label>
                            <input
                                id="reason"
                                type="text"
                                className="form-control"
                                value={formData.reason}
                                onChange={onReasonChange}
                            />
                            {errors.reason && (
                                <span className="help-block margin-bottom-10">
                                    <span>{errors.reason}</span>
                                </span>
                            )}
                        </div>
                        </div>
                    </>
                }
                bsSize={Dialog.SIZE_SM}
                onClickConfirm={handleActionConfirmed}
                onClickCancel={() => setShowConfirmationDialog(false)}
                cancelButtonText="Cancel"
                confirmButtonText={
                    <>
                        <span className="rioglyph rioglyph-ok margin-right-5" />
                        <span>{isChangingVinList ? 'Processing...' : 'Confirm'}</span>
                    </>
                }
                useOverflow
            />
            <div className="margin-top-20">
                <h3>
                    <FormattedMessage id="intl-msg:coreconAdminWeb.connectivityInterruption.historical.data" />
                </h3>
                <ListTable<VinBlacklistModel>
                    items={logItems}
                    showHeader
                    itemKey="listName"
                    columnDescriptors={columnDescriptorsLogs()}
                />
            </div>
        </>
    );
};
